import { useContext, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { Loader2, Upload } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSlot } from '../../components/sidebarSlot';
import { CallModal } from './CallBadge';
import { TIP_BADGE } from './HoverTip';
import { invalidateCatalogItems } from './catalogItems';
import { FolderDoc } from './noticeStore';
import { addParts } from './partsRun';
import { useProductLabel } from './ProductColumn';
import { falError, importOf, importProduct, nameImport, type ImportedObject, type Job3D, type PieceItem } from './threeDApi';

// THE WHOLE PRODUCT, MODELLED BY ITS MAKER, brought in instead of written (Louis 2026-10-28).
// A badge in the phase's row beside 3D model generation (IMPORT_SLOT_ID, in ModelsPhase), and a
// window in two steps: the file, which the server turns into a product run with the file's own
// object names (see import_product); then which part each object is, matched by name or letter
// where the file says, by hand where it does not. Naming renames the run's objects to the parts'
// names (see name_import), and the run's switch in the strip keeps it, cutting it into its parts
// as it cuts a written one.
export const IMPORT_SLOT_ID = 'models-import-slot';

// The formats Blender reads on the server (blender_ops.IMPORT_FORMATS).
const ACCEPT = '.glb,.gltf,.obj,.stl,.fbx';

type Part = PieceItem & { letter: string | null };

// A NAME AS TWO PROGRAMS WOULD AGREE ON IT: no case, no accents, no Blender ".001", separators as
// spaces.
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/\.\d+$/, '').replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ').trim();

// WHICH PART AN OBJECT IS, BY ITS NAME: the part's name exactly, then the longest part name inside
// the object's ("Back leg left" is a back leg), then the manual's letter as a word of its own
// ("A", "Part_B"). '' where none says.
function matchOf(object: string, parts: Part[]): string {
  const name = norm(object);
  const exact = parts.find((p) => norm(p.name) === name);
  if (exact) return exact.name;
  const inside = parts.filter((p) => norm(p.name) && ` ${name} `.includes(` ${norm(p.name)} `))
    .sort((a, b) => b.name.length - a.name.length)[0];
  if (inside) return inside.name;
  const words = name.split(' ');
  return parts.find((p) => p.letter && words.includes(p.letter.toLowerCase()))?.name ?? '';
}

// THE NAMES THE OBJECTS ARE GIVEN. A part two objects are is a pair, the left hand (−x, the app's
// left) and the right: the kept model is the left one, the right its reflection (see _hand).
// Three or more of one part are the same part again, the first kept.
function namesOf(objects: ImportedObject[], picks: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  const byPart = new Map<string, ImportedObject[]>();
  for (const o of objects) {
    const part = picks[o.name];
    if (part) byPart.set(part, [...(byPart.get(part) ?? []), o]);
  }
  for (const [part, of] of byPart) {
    if (of.length === 2) {
      const [left, right] = [...of].sort((a, b) => a.centreMm[0] - b.centreMm[0]);
      out[left.name] = `${part} (Left)`;
      out[right.name] = `${part} (Right)`;
    } else {
      for (const o of of) out[o.name] = part;
    }
  }
  return out;
}

// THE CHOICE THAT IS NOT A PART: this object is the whole product, uncut. Several can be (the
// same chair in three finishes): each becomes a run of its own (see _keep_whole).
const WHOLE = '\u0000whole';

// AND IN A FOLDER, WHICH HAS NO PARTS LIST: a part made of the object, named here (see addParts).
const NEW = '\u0000new';
// The name an object is offered as a part under: the file's, without Blender's ".001".
const partNameOf = (object: string) => object.replace(/\.\d+$/, '').replace(/_+/g, ' ').trim();

// A FILE THAT DOES NOT SEPARATE ITS PARTS, read off its objects: one object, or several of one size
// (the same chair in three finishes, side by side). Each of them is then the product (see begin).
function wholeFile(objects: ImportedObject[]): boolean {
  if (objects.length <= 1) return true;
  const [first] = objects;
  return objects.every((o) => o.sizeMm.every((v, i) => Math.abs(v - first.sizeMm[i]) <= 0.1 * Math.max(v, first.sizeMm[i])));
}

const cm = (mm: number[]) => `${mm.map((v) => Math.round(v / 10)).join(' × ')} cm`;

export function ProductImport({ docId, submodel, parts, shown, onImported }: {
  docId: string;
  submodel: string;
  // The component parts an object can be, with their catalog rows (see componentRows).
  parts: Part[];
  // The run on screen: an import not kept yet opens on its names, to be named again.
  shown: Job3D | null;
  onImported: (job: Job3D) => void;
}) {
  const { t } = useLanguage();
  const folder = useContext(FolderDoc);
  const label = useProductLabel(docId);
  const slot = useSlot(IMPORT_SLOT_ID);
  // The new parts' names, in a folder, by object.
  const [newNames, setNewNames] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);
  const [job, setJob] = useState<Job3D | null>(null);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // THE FILE BEING READ, said while it is: a big one takes a while, and the button alone did not
  // say which file it was busy with.
  const [reading, setReading] = useState('');
  const [error, setError] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const mark = importOf(job);

  const begin = (on: Job3D | null) => {
    setJob(on);
    setError('');
    const said = importOf(on);
    // WHAT IT WAS NAMED LAST, where it was; the match by name where it was not; and a file that
    // matches nothing and does not separate its parts, each of its objects as the whole product.
    const objects = said?.objects ?? [];
    const picked = Object.fromEntries(objects.map((o) => {
      const named = said?.names[o.name];
      return [o.name, named ? named.replace(/ \((Left|Right)\)$/, '') : matchOf(o.name, parts)];
    }));
    // EVERY ONE OF THEM, for a file that does not separate its parts: several objects of one size are
    // the same product in several finishes, each a model of its own (Louis 2026-10-29). Kept already,
    // the one object it was cut to.
    if (objects.length && (said?.whole || (!Object.values(picked).some(Boolean) && wholeFile(objects)))) {
      for (const o of objects) picked[o.name] = WHOLE;
    } else if (folder) {
      for (const o of objects) if (!picked[o.name]) picked[o.name] = NEW;
    }
    setPicks(picked);
    setNewNames(Object.fromEntries(objects.map((o) => [o.name, partNameOf(o.name)])));
  };
  const openWindow = () => {
    begin(importOf(shown) && !shown?.inCatalog ? shown : null);
    setOpen(true);
  };
  const upload = async (file: File) => {
    setBusy(true);
    setReading(file.name);
    setError('');
    try {
      const made = await importProduct(file, docId, submodel);
      begin(made);
      onImported(made);
    } catch (e) {
      setError(falError(e, t('notice.import.failed')));
    } finally {
      setBusy(false);
      setReading('');
    }
  };
  const save = async () => {
    if (!job || !mark) return;
    setBusy(true);
    setError('');
    try {
      let named = picks;
      // THE NEW PARTS FIRST, a catalog row each and a line of the parts list, so the run's
      // objects are named after parts that exist. Filed under the product's name as their
      // category: two folders' "Leg" are two parts, and the catalog keeps one row per name and
      // category (see create_item).
      if (!wholes.length && Object.values(picks).includes(NEW)) {
        const fresh = [...new Set(mark.objects.filter((o) => picks[o.name] === NEW)
          .map((o) => (newNames[o.name] || '').trim() || partNameOf(o.name)))];
        const made = await Promise.all(fresh.map(async (name) => {
          const { data } = await axios.post<{ id: number }>('/api/notice/catalog', {
            kind: 'component', nameEn: name, nameFr: name, category: label, docId,
          });
          return { name, catalogId: data.id };
        }));
        addParts(docId, made);
        invalidateCatalogItems();
        named = Object.fromEntries(Object.entries(picks).map(([k, v]) => [k,
          v === NEW ? (newNames[k] || '').trim() || partNameOf(k) : v]));
      }
      onImported(await nameImport(job.id, wholes.length
        ? { whole: wholes.map((o) => o.name) } : { names: namesOf(mark.objects, named) }));
      setOpen(false);
    } catch (e) {
      setError(falError(e, t('notice.import.failed')));
    } finally {
      setBusy(false);
    }
  };

  const overall = useMemo(() => {
    const objects = mark?.objects ?? [];
    if (!objects.length) return null;
    const lo = [0, 1, 2].map((i) => Math.min(...objects.map((o) => o.centreMm[i] - o.sizeMm[i] / 2)));
    const hi = [0, 1, 2].map((i) => Math.max(...objects.map((o) => o.centreMm[i] + o.sizeMm[i] / 2)));
    return cm(hi.map((v, i) => v - lo[i]));
  }, [mark]);
  const matched = mark ? mark.objects.filter((o) => picks[o.name] && picks[o.name] !== WHOLE).length : 0;
  // THE OBJECTS KEPT AS THE WHOLE PRODUCT, where there are: the rest of the file is dropped, so it
  // is that or parts, never both — choosing one kind clears the other.
  const wholes = mark?.objects.filter((o) => picks[o.name] === WHOLE) ?? [];
  const pick = (object: string, value: string) => setPicks((was) => Object.fromEntries(
    Object.entries({ ...was, [object]: value }).map(([k, v]) => [k,
      k === object || !v || (value === WHOLE) === (v === WHOLE) ? v : '']),
  ));

  const chooser = (
    <>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void upload(file);
        }}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => input.current?.click()}
        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-blue-500 hover:text-blue-600 disabled:opacity-50"
      >
        {reading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {reading ? t('notice.import.reading').replace('{file}', reading) : t(mark ? 'notice.import.another' : 'notice.import.choose')}
      </button>
    </>
  );

  const th = 'px-3 py-1.5 text-center text-xs font-semibold text-slate-600';
  const td = 'px-3 py-1.5 text-center text-xs text-slate-800';
  return (
    <>
      {slot && createPortal(
        <button type="button" onClick={openWindow} className={`${TIP_BADGE} gap-1.5 pl-2.5`}>
          <span className="text-[10px] font-bold uppercase tracking-wider">{t('notice.import.badge')}</span>
          <Upload className="h-3.5 w-3.5" />
        </button>,
        slot,
      )}
      {open && (
        <CallModal title={t('notice.import.badge')} copy={false} onClose={() => setOpen(false)}>
          <div className="flex flex-col items-center gap-4">
            <h4 className="text-center text-[11px] font-bold uppercase tracking-wider text-slate-500">
              {t('notice.import.badge')}
            </h4>
            {!mark && <p className="max-w-md text-center text-sm text-slate-600">{t('notice.import.intro')}</p>}
            {!mark && chooser}
            {mark && (
              <>
                <p className="text-center text-sm text-slate-600">
                  {t('notice.import.whole').replace('{file}', mark.filename).replace('{size}', overall ?? '')}
                </p>
                <p className="max-w-md text-center text-xs font-semibold text-slate-500">
                  {wholes.length ? t(wholes.length > 1 ? 'notice.import.wholesSaid' : 'notice.import.wholeSaid')
                    : t('notice.import.matched').replace('{n}', String(matched)).replace('{m}', String(mark.objects.length))}
                </p>
                <table className="w-full max-w-2xl">
                  <thead>
                    <tr className="border-b border-slate-300">
                      <th className={th}>{t('notice.import.object')}</th>
                      <th className={th}>{t('notice.import.size')}</th>
                      <th className={th}>{t('notice.import.part')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {mark.objects.map((o) => (
                      <tr key={o.name}>
                        <td className={td}>{o.name}</td>
                        <td className={`${td} tabular-nums text-slate-500`}>{cm(o.sizeMm)}</td>
                        <td className={td}>
                          <select
                            value={picks[o.name] ?? ''}
                            onChange={(e) => pick(o.name, e.target.value)}
                            className={`rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs ${picks[o.name] ? 'text-slate-800' : 'text-slate-400'}`}
                          >
                            <option value="">{t('notice.import.none')}</option>
                            <option value={WHOLE}>{t('notice.import.wholeOption')}</option>
                            {folder && <option value={NEW}>{t('notice.import.newPart')}</option>}
                            {parts.map((p) => (
                              <option key={p.itemId} value={p.name}>{p.letter ? `${p.name} (${p.letter})` : p.name}</option>
                            ))}
                          </select>
                          {picks[o.name] === NEW && (
                            <input
                              value={newNames[o.name] ?? ''}
                              onChange={(e) => setNewNames((was) => ({ ...was, [o.name]: e.target.value }))}
                              placeholder={t('notice.import.newPartName')}
                              className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-800"
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  type="button"
                  disabled={busy || (!matched && !wholes.length)}
                  onClick={() => void save()}
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {wholes.length > 1 ? t('notice.import.saveWholes').replace('{n}', String(wholes.length))
                    : t(wholes.length ? 'notice.import.saveWhole' : 'notice.import.save')}
                </button>
                {chooser}
              </>
            )}
            {error && <p className="text-center text-sm text-rose-600">{error}</p>}
          </div>
        </CallModal>
      )}
    </>
  );
}
